import { Component, Input, ViewChild, OnInit, ElementRef, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, fromEvent as observableFromEvent, merge as observableMerge, combineLatest } from 'rxjs';
import { map, mapTo, startWith } from 'rxjs/operators';
import { VaProductNavService } from './va-product-nav.service';
import { VaProductNavItem } from './va-product-nav-item';

@Component({
  selector: 'va-product-nav-item',
  template: `
    <div
      #navItemRef
      class="nav-item"
      [style.background-color]="navItem.selected && !hasChildren ? highlightColor : (highlight$ | async)"
      >
      <div class="expand-icon-section" (click)="toggleExpand()">
        <mat-icon [ngClass]="{ invisible: !hasChildren || !navExpanded, rotate: expanded }">arrow_right</mat-icon>
      </div>
      <div
        class="nav-item-section"
        [routerLink]="navItem.url"
        tabindex="-1"
        (click)="navItem.url ? null : toggleExpand()"
        >
        @if (!navItem.isIconSvg) {
          @if (
            navItem.icon && !isChild && (badgeValueForParent$ | async) !== null && (badgeValueForParent$ | async) > 0
            ) {
            <mat-icon
              [matBadge]="badgeValueForParent$ | async"
              [matBadgeColor]="navItem.badgeColor || 'primary'"
              matBadgeSize="small"
              matBadgePosition="below after"
              >
              {{ navItem.icon }}
            </mat-icon>
          }
          @if (
            navItem.icon &&
            !isChild &&
            ((badgeValueForParent$ | async) === null || (badgeValueForParent$ | async) === 0)
            ) {
            <mat-icon
              >
              {{ navItem.icon }}
            </mat-icon>
          }
        }
        @if (navItem.isIconSvg) {
          @if (
            navItem.icon && !isChild && (badgeValueForParent$ | async) !== null && (badgeValueForParent$ | async) > 0
            ) {
            <mat-icon
              [matBadge]="badgeValueForParent$ | async"
              [matBadgeColor]="navItem.badgeColor || 'primary'"
              matBadgeSize="small"
              matBadgePosition="below after"
              [svgIcon]="navItem.icon"
              />
          }
          @if (
            navItem.icon &&
            !isChild &&
            ((badgeValueForParent$ | async) === null || (badgeValueForParent$ | async) === 0)
            ) {
            <mat-icon
              [svgIcon]="navItem.icon"
              />
          }
        }
        @if (isChild) {
          <div class="child-spacer"></div>
        }
        <a
          class="label"
          [routerLink]="navItem.url"
          [class.selected-parent]="navItem.selected"
          [routerLinkActive]="isChild ? 'selected' : ''"
          (keyup.enter)="routerNav(navItem.url)"
          [routerLinkActiveOptions]="{ exact: isChild }"
          (click)="navItemClicked.emit(navItem)"
          >
          {{ navItem.label | translate }}
        </a>
      </div>
      @if (badgeValue$ | async; as bv) {
        <div
          class="badge"
          [matBadge]="bv"
          [matBadgeColor]="navItem.badgeColor || 'primary'"
        ></div>
      }
      @if (navItem.showUpgradeChip && upgradeChipText) {
        <glxy-badge color="blue">
          {{ upgradeChipText }}
        </glxy-badge>
      }
      @if (navItem.newChipText) {
        <glxy-badge color="green">
          {{ navItem.newChipText | translate }}
        </glxy-badge>
      }
    </div>
    @if (navExpanded && expanded) {
      @for (child of navItem.children; track child) {
        <va-product-nav-item
          [navItem]="child"
          [highlightColor]="highlightColor"
          [badgeId]="child.badgeId"
          [isChild]="true"
          [upgradeChipText]="upgradeChipText"
          />
      }
    }
    `,
  styleUrls: ['./va-product-nav-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VaProductNavItemComponent implements OnInit {
  @Input() navItem: VaProductNavItem;
  @Input() isChild = false;
  @Input() navExpanded = true;
  @Input() highlightColor = 'rgba(0, 0, 0, 0.1)';
  @Input() badgeId: string;
  @Input() upgradeChipText: string;

  // navItemClicked emits an event whenever a nav item's anchor tag is clicked
  @Output() navItemClicked = new EventEmitter<VaProductNavItem>();

  @ViewChild('navItemRef', { static: true }) navItemRef: ElementRef;

  expanded = false;
  hasChildren = false;
  highlight$: Observable<string>;
  badgeValue$: Observable<number | null>;
  childrenBadgeTotal$: Observable<number>;
  badgeValueForParent$: Observable<number | null>;

  constructor(
    private router: Router,
    private navService: VaProductNavService,
  ) {}

  ngOnInit(): void {
    this.hasChildren = this.navItem.children! && this.navItem.children.length > 0;
    this.badgeValue$ = this.navService.navBadges$.pipe(
      map((badges) =>
        this.badgeId
          ? Object.prototype.hasOwnProperty.call(badges, this.badgeId)
            ? badges[this.badgeId]
            : null
          : null,
      ),
    );
    this.childrenBadgeTotal$ = this.navService.navBadges$.pipe(
      map((badges) =>
        (this.navItem.children || [])
          .map((child) => {
            return child.badgeId
              ? Object.prototype.hasOwnProperty.call(badges, child.badgeId)
                ? badges[child.badgeId]
                : 0
              : 0;
          })
          .reduce((a, b) => a + b, 0),
      ),
    );
    this.badgeValueForParent$ = combineLatest(this.badgeValue$, this.childrenBadgeTotal$).pipe(
      map(([badgeValue, childrenBadgeValue]) => this.getBadgeValueForParent(badgeValue, childrenBadgeValue)),
    );
    const hoverIn$ = observableFromEvent(this.navItemRef.nativeElement, 'mouseover').pipe(mapTo(true));
    const hoverOut$ = observableFromEvent(this.navItemRef.nativeElement, 'mouseleave').pipe(mapTo(false));
    const hovering$ = observableMerge(hoverIn$, hoverOut$).pipe(startWith(false));
    this.highlight$ = hovering$.pipe(
      map((hovering) => (!this.hasChildren && hovering ? this.highlightColor : 'transparent')),
    );
    this.expanded = this.isChild
      ? false
      : this.hasChildren && this.navItem.children!.filter((c) => c.selected).length > 0;
    this.router.events
      .pipe(
        map(
          () =>
            (this.expanded = this.isChild
              ? false
              : this.hasChildren && this.navItem.children!.filter((c) => c.selected).length > 0),
        ),
      )
      .subscribe();
  }

  toggleExpand(): void {
    if (this.hasChildren) {
      this.expanded = !this.expanded;
    }
  }

  routerNav(url: string): void {
    /* specifcally for accessibility, pressing enter when nav items are tabbed */
    this.router.navigateByUrl(url);
  }

  getBadgeValueForParent(badgeValue, childrenBadgeValue): number | null {
    if (!badgeValue && !childrenBadgeValue) {
      return null;
    }
    if ((!this.expanded && this.hasChildren) || !this.navExpanded) {
      return badgeValue ? badgeValue : childrenBadgeValue;
    }
    return null;
  }
}
